/* =========================================================================
 * THE HUNT BAR — box select, and hand the whole selection to the hunt.
 *
 * Brian: "if a sales rep has 50 championships assigned and they have to
 * manually ask our command center to go fucking hunt and find them, thats what
 * they should be able to do."
 *
 * A per-row button was a chore. Fifty rows is fifty clicks, and nobody does that
 * twice — so the unit of work here is THE SELECTION.
 *
 * ⚠️ IT QUEUES; IT NO LONGER BUYS FIFTY SEARCHES.
 *
 * Brian: "Nothing should be routed through paid api with Claude or OpenAI."
 * Brian: "the hand hunt on the subscription is the route every one of these
 * tasks must take."
 *
 * This bar used to POST /api/champion-hunt, which bills per record — so one
 * drag-select of fifty rows was fifty metered searches behind a single click,
 * with no estimate and no ceiling in front of it. That is the exact shape of
 * spend this system is not allowed to have.
 *
 * It now calls POST /api/champion-request per selected record, which costs
 * nothing: it marks each one `sr_champion_search: 'requested'`, and the
 * hand-hunt sweep — run on a subscription SR already pays for — works requested
 * records FIRST. The rep's action is unchanged (select fifty, press once); what
 * changed is that pressing it cannot spend money.
 *
 * SEQUENTIAL, ON PURPOSE. These are writes to the system of record and a burst
 * of parallel PATCHes is how you find a rate limit with your face. It also
 * means the count on screen is honest as it climbs, and every refusal comes
 * back attached to the record it belongs to — "already names Cape Catfish" is
 * something a rep can act on; a single failure count is not.
 *
 * BOUNDED. A selection of thousands is not a click anybody meant to make, so
 * the bar states the cap and queues up to it rather than walking the board.
 * ========================================================================= */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { requestHunt } from './huntJob.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './pursuit.module.css'

/* One press should not be able to touch an unbounded number of records. */
export const QUEUE_CAP = 250

export default function HuntBar({ selection, onOpen, onAccepted }) {
  const { can, why } = useAccess()
  const [run, setRun] = useState(null)   // { done, total, queued, already, refused[] }
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(true)

  const count = selection?.count || 0
  /* Asking costs nothing and writes no champion, so it rides the same
   * permission as recording one. The server re-decides it per record — this is
   * a DRAWING decision (hard rule 5), and with a mixed selection some rows may
   * still come back refused, which is why refusals are listed individually. */
  const mayAsk = can(ACTIONS.CC_CHAMPION_UPDATE)

  const start = async () => {
    setError(null)
    const list = (selection?.selected || []).slice(0, QUEUE_CAP)
    if (!list.length) { setError('Nothing selected.'); return }
    const state = { done: 0, total: list.length, queued: 0, already: 0, refused: [] }
    setRun({ ...state })
    setOpen(true)
    for (const c of list) {
      try {
        const r = await requestHunt(String(c.id))
        r?.alreadyQueued ? state.already++ : state.queued++
        /* Repaint the row so it does not keep reading as unasked next to one we
         * just queued — the same two-sources-of-truth fault as everywhere else
         * here. */
        onAccepted && onAccepted(String(c.id), { championSearchTag: 'requested' })
      } catch (e) {
        state.refused.push({
          id: String(c.id),
          name: c.name || c.championshipName || String(c.id),
          /* The server's own words, unedited. */
          reason: e?.body?.reason || e?.message || 'Could not queue it.',
        })
      }
      state.done++
      setRun({ ...state, refused: [...state.refused] })
    }
  }

  /* IT IS A DOCK, NOT A BANNER — see .huntBar in the stylesheet.
   *
   * Three attempts, measured each time on the live board: rendering null shifted
   * the table 81px on first tick; reserving space in this bar left 36px; both
   * still left 7px, because an idle twin only matches its active version until
   * someone edits one of them. Pixel-matching a placeholder to a thing that
   * grows is a losing game, so the bar is fixed to the bottom of the viewport
   * and cannot move the rows by any amount. It also stays reachable while a rep
   * scrolls a 13,000-row board, which is what a bulk action needs anyway. */
  if (!count && !run) return null

  const running = run && run.done < run.total

  /* PORTALLED TO <body>, and this is not optional.
   *
   * Docking it fixed stopped the board shifting but made the bar INVISIBLE, and
   * the measurement said exactly why: an ancestor `.fade-up` animation wrapper
   * keeps `transform: matrix(1,0,0,1,0,0)` after it finishes. An identity matrix
   * is still a transform, and any transform makes that element the containing
   * block for fixed descendants — so the dock was pinned to the bottom of a
   * 63,782px-tall div, ~62,000px below the fold.
   *
   * `position: fixed` is only trustworthy when nothing above you has a
   * transform, and nothing stops someone adding one later. A portal to body
   * cannot be broken by an ancestor that does not exist. */
  return createPortal((
    <div className={styles.huntBar}>
      <div className={styles.huntTop}>
        <span className={styles.huntCount}>
          {run ? `${run.done} of ${run.total} queued` : `${count} selected`}
        </span>

        {!run && mayAsk && (
          <button type="button" className={styles.huntGo} onClick={start}>
            Queue {Math.min(count, QUEUE_CAP)} for the hand hunt
          </button>
        )}

        {!run && !mayAsk && (
          <span className={styles.huntWhy}>
            {why(ACTIONS.CC_CHAMPION_UPDATE) || 'Champion changes are not available for your role.'}
          </span>
        )}

        {!run && count > QUEUE_CAP && (
          <span className={styles.huntWhy}>
            {count} selected — {QUEUE_CAP} is the cap per press.
          </span>
        )}

        {running && <span className={styles.huntRunning}>queueing…</span>}

        {run && !running && (
          <>
            <span className={styles.huntDone}>
              {run.queued} queued{run.already ? ` · ${run.already} already` : ''}
              {run.refused.length ? ` · ${run.refused.length} refused` : ''}
            </span>
            {mayAsk && <button type="button" className={styles.huntGo} onClick={start}>Run again</button>}
          </>
        )}

        {run && run.refused.length > 0 && (
          <button type="button" className={styles.huntGhost} onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Show'} refusals
          </button>
        )}
        {count > 0 && (
          <button type="button" className={styles.huntGhost} onClick={() => selection.clear()}>Clear</button>
        )}
      </div>

      {error && <div className={styles.huntErr}>{error}</div>}

      {run && !running && run.refused.length === 0 && run.queued > 0 && (
        <div className={styles.huntResults}>
          <div className={styles.huntRowMuted}>
            Queued. The hand hunt works requested records first — nothing here was
            charged per record, and no champion is written until a person records
            one with its source.
          </div>
        </div>
      )}

      {/* THE ONES IT WOULD NOT QUEUE, WITH THE REASON. A blank row teaches
          people the feature is broken; a stated refusal teaches them what the
          record needs — and "already names X" is usually the answer, not a
          fault. */}
      {run && open && run.refused.length > 0 && (
        <div className={styles.huntResults}>
          {run.refused.map((r) => (
            <div key={r.id} className={`${styles.huntRow} ${styles.huntRowMuted}`}>
              <button type="button" className={styles.huntName} onClick={() => onOpen && onOpen({ id: r.id })}>
                {r.name}
              </button>
              <span className={styles.huntWhy}>{r.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  ), document.body)
}
